import { apiGetInvoices } from "@lib/api/billing";
import { AppCard } from "@lib/components/AppComponents";
import { ApiParams } from "@lib/types";
import { Invoice, WalletAddressType } from "@lib/types/Billing";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import dayjs from "dayjs";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useTheme } from "styled-components";
import { numberFormat } from "@lib/helpers/StringHelper";
import { Empty, Skeleton } from "antd";
import { useIntl } from "react-intl";
import { green, red } from "@ant-design/colors";
import { ActionButton } from "../Buttons";

type MonthlyRevenue = {
  month: string;
  sortKey: string; // For proper date sorting
  datetime: string;
  [key: string]: string | number | undefined; // Dynamic currency fields
};

const currencySymbols: Partial<Record<WalletAddressType, string>> = {
  [WalletAddressType.USDT]: "$",
};

const currencyColors: Partial<
  Record<WalletAddressType, { paid: string; unpaid: string }>
> = {
  [WalletAddressType.USDT]: {
    paid: green[6],
    unpaid: red[6],
  },
};

type CurrencyData = {
  total: number;
  paid: number;
};

export default function MonthlyInvoiceChart() {
  const { mode } = useTheme();
  const { messages } = useIntl();
  const params = useMemo(() => {
    return {
      limit: 1000, // Get all invoices for calculation
    } as ApiParams;
  }, []);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryFn: () => apiGetInvoices(params),
    queryKey: ["invoice-chart", JSON.stringify(params)],
    enabled: !!params,
  });

  // Get unique currencies from API data
  const availableCurrencies = useMemo(() => {
    if (!data?.data?.data) return [];
    const invoices: Invoice[] = data.data.data;
    const currencies = new Set<WalletAddressType>();
    invoices.forEach((invoice) => {
      if (invoice.currency) {
        currencies.add(invoice.currency);
      }
    });
    return Array.from(currencies);
  }, [data]);

  const monthlyData = useMemo(() => {
    if (!data?.data?.data) return [];

    const invoices: Invoice[] = data.data.data;
    // Map structure: monthKey -> currency -> {total, paid}
    const monthlyMap = new Map<string, Map<WalletAddressType, CurrencyData>>();

    // Group invoices by month and currency, calculate total and paid amounts
    invoices.forEach((invoice) => {
      if (invoice.createdAt && invoice.currency) {
        const date = dayjs(invoice.createdAt);
        const monthKey = date.format("YYYY-MM");

        if (!monthlyMap.has(monthKey)) {
          monthlyMap.set(monthKey, new Map<WalletAddressType, CurrencyData>());
        }

        const currencyMap = monthlyMap.get(monthKey)!;
        const currentData = currencyMap.get(invoice.currency) || {
          total: 0,
          paid: 0,
        };

        currencyMap.set(invoice.currency, {
          total: currentData.total + invoice.amount,
          paid: currentData.paid + invoice.paid,
        });
      }
    });

    // Convert map to array and sort by date
    const monthlyArray: MonthlyRevenue[] = Array.from(monthlyMap.entries())
      .map(([monthKey, currencyMap]) => {
        const date = dayjs(monthKey);
        const monthName = date.format("MMMM YYYY");

        const revenueData: MonthlyRevenue = {
          month: monthName,
          sortKey: monthKey,
          datetime: dayjs(date).date(15).toISOString(),
        };

        // Add total, paid and unpaid amounts for each currency dynamically
        currencyMap.forEach((data, currency) => {
          const total = Math.round(data.total * 100) / 100;
          const paid = Math.round(data.paid * 100) / 100;
          const unpaid = Math.round((data.total - data.paid) * 100) / 100;
          revenueData[`${currency}_total`] = total;
          revenueData[`${currency}_paid`] = paid;
          revenueData[`${currency}_unpaid`] = unpaid;
        });

        return revenueData;
      })
      .sort((a, b) => {
        // Sort by sortKey (YYYY-MM format) for proper chronological order
        return a.sortKey.localeCompare(b.sortKey);
      });

    return monthlyArray;
  }, [data]);

  // Prepare ApexCharts series and options
  const chartSeries = useMemo(() => {
    const series: Array<{ name: string; data: number[] }> = [];

    availableCurrencies.forEach((currency) => {
      series.push({
        name: `${currency} ${currencySymbols[currency]} - ${messages["common.paid"]}`,
        data: monthlyData.map((item) => Number(item[`${currency}_paid`]) || 0),
      });
      series.push({
        name: `${currency} ${currencySymbols[currency]} - ${messages["common.unpaid"]}`,
        data: monthlyData.map(
          (item) => Number(item[`${currency}_unpaid`]) || 0,
        ),
      });
    });

    return series;
  }, [monthlyData, availableCurrencies]);

  const chartOptions: ApexOptions = useMemo(() => {
    const colors: string[] = [];

    availableCurrencies.forEach((currency) => {
      const currencyColor = currencyColors[currency] || {
        paid: "#82ca9d",
        unpaid: "#8884d8",
      };
      colors.push(currencyColor.paid, currencyColor.unpaid);
    });

    return {
      series: chartSeries,
      chart: {
        type: "bar",
        height: 350,
        width: "100%",
        stacked: true,
        toolbar: {
          show: true,
        },
        zoom: {
          enabled: true,
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: "bottom",
              offsetX: -10,
              offsetY: 0,
            },
          },
        },
      ],
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 10,
          borderRadiusApplication: "end", // 'around', 'end'
          borderRadiusWhenStacked: "last", // 'all', 'last'
          dataLabels: {
            total: {
              enabled: true,
              style: {
                fontSize: "13px",
                fontWeight: 900,
                color: mode === "dark" ? "#fff" : "#000",
              },
            },
          },
        },
      },
      xaxis: {
        type: "datetime",
        categories: monthlyData.map((item) => item.datetime),
        labels: {
          rotate: -55,
          rotateAlways: true,
          trim: true,
          minHeight: 70,
          style: {
            fontSize: "11px",
          },
          formatter: (value: string) => {
            return dayjs(value).format("MMMM");
          },
        },
      },
      yaxis: {
        labels: {
          formatter: (val: number) => {
            return numberFormat(val / 1000, 0) + "K";
          },
        },
      },
      colors: colors.length > 0 ? colors : undefined,
      tooltip: {
        y: {
          formatter: (value: number, { seriesIndex, w }) => {
            const series = w.globals.seriesNames[seriesIndex];
            if (series) {
              const currencyMatch = series.match(/(BTC|ETH|USDT)/);
              const symbol = currencyMatch
                ? currencySymbols[currencyMatch[0] as WalletAddressType]
                : "";
              return `${value.toLocaleString("tr-TR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} ${symbol}`;
            }
            return value.toLocaleString("tr-TR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          },
        },
        shared: true,
        intersect: false,
      },
      legend: {
        position: "top",
        horizontalAlign: "left",
      },
      dataLabels: {
        formatter: (val: number) => {
          return numberFormat(val / 1000, 0) + "K";
        },
      },
      fill: {
        opacity: 1,
      },
      theme: {
        mode: mode,
      },
    };
  }, [monthlyData, availableCurrencies, mode]);

  return (
    <AppCard
      title={messages["common.monthlyInvoiceChart"] as string}
      extra={
        <ActionButton
          type="refresh"
          size="small"
          onClick={() => refetch()}
          disabled={isLoading || isFetching}
          loading={isLoading || isFetching}
        />
      }
    >
      {isLoading || isFetching ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <>
          {monthlyData.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <Empty />
            </div>
          ) : (
            <Chart
              options={chartOptions}
              series={chartSeries}
              type="bar"
              height={400}
              width="100%"
            />
          )}
        </>
      )}
    </AppCard>
  );
}
